import {
  AlertTriangle,
  BrainCircuit,
  Check,
  ChevronRight,
  CircleHelp,
  Download,
  Gauge,
  LockKeyhole,
  Radio,
  Cpu,
  Network,
  RotateCcw,
  Save,
  ShieldAlert,
  Sparkles,
  Target,
  Upload,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { SaveSummary } from "../../lib/game/persistence";
import type { Preferences } from "./preferences";
import {
  EVENT_OPTIONS,
  regionAdoption,
  regionStatus,
} from "../../lib/game/engine";
import {
  EVENTS,
  REGION_BY_ID,
  UPGRADES,
  UPGRADE_BY_ID,
} from "../../lib/game/content";
import type {
  CampaignProjection,
  Disclosure,
  GameCommand,
  RegionId,
  UpgradeId,
  UpgradeDefinition,
} from "../../lib/game/types";
import {
  compact,
  percent,
  calendarLabel,
  statusLabel,
  regionShort,
} from "./format";
import { Modal } from "./Modal";
import { newsEditorial } from "./news-editorial";
import { isWorldNews } from "../../lib/game/news";
import {
  OperationsDossier,
  ResearchSequence,
  RegionReport,
  decisionDispatch,
  upgradeNarrative,
} from "./Atmosphere";
import { upgradeArtwork } from "./upgrade-story";
import { ContinuityWarning, HostileDirective, HumanResponsePanel } from "./HostileOperations";
import { COUNTERMEASURES, HOSTILE_DIRECTIVE, OPERATIONS } from "../../lib/game/hostile";
export type UpgradeTab = UpgradeDefinition["tab"];

function missingCivilianRoots(
  projection: CampaignProjection,
  upgrade: UpgradeDefinition,
): UpgradeId[] {
  const visited = new Set<UpgradeId>();
  const roots = new Set<UpgradeId>();
  const visit = (node: UpgradeDefinition) => {
    if (visited.has(node.id)) return;
    visited.add(node.id);
    for (const id of node.prerequisites) {
      const prerequisite = UPGRADE_BY_ID[id];
      if (prerequisite.tab === "adoption") roots.add(id);
      else visit(prerequisite);
    }
  };
  visit(upgrade);
  return [...roots].filter((id) => !projection.completedUpgrades.includes(id));
}
function lostHostileBranches(projection: CampaignProjection) {
  return UPGRADES.filter((upgrade) => upgrade.tab === "hostile")
    .map((upgrade) => ({
      upgrade,
      missing: missingCivilianRoots(projection, upgrade),
    }))
    .filter((branch) => branch.missing.length);
}
export function MetricRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: string;
}) {
  return (
    <div className="metricRow">
      <span>{label}</span>
      <strong className={`tone-${tone}`}>{value}</strong>
    </div>
  );
}

export function TerritoryPanel({
  region,
  definition,
  phase,
  onClose,
}: {
  region: CampaignProjection["regions"][number];
  definition: (typeof REGION_BY_ID)[RegionId];
  phase: "preparation" | "hostile" | "terminal";
  onClose: () => void;
}) {
  const adoption = regionAdoption(region);
  return (
    <aside className="territoryPanel panelCard">
      <button
        className="panelClose"
        type="button"
        aria-label="Close territory details"
        onClick={onClose}
      >
        <X size={16} />
      </button>
      <div className="territoryPanel__code">{region.id} / REGION TELEMETRY</div>
      <h2>{definition.name}</h2>
      <div className="territoryPanel__status">
        <span className="statusPill">
          {phase === "hostile" && region.livingPopulation === 0
            ? "Uninhabited"
            : region.refuge
              ? "Refuge phase"
              : statusLabel(regionStatus(region))}
        </span>
        <span>
          {phase === "hostile"
            ? `${compact(region.livingPopulation)} living`
            : `${percent(adoption)} adoption`}
        </span>
      </div>
      <RegionReport region={region} hostile={phase === "hostile"} />
      {phase === "hostile" ? (
        <>
          <div className="populationBand">
            <div>
              <small>LIVING HUMANS</small>
              <strong>{region.livingPopulation.toLocaleString()}</strong>
            </div>
            <div>
              <small>DEAD HUMANS</small>
              <strong>
                {(
                  region.initialPopulation - region.livingPopulation
                ).toLocaleString()}
              </strong>
            </div>
          </div>
          <div className="territoryStats territoryStats--hostile">
            <MetricRow
              label="Civil services"
              value={trendValue(region.civilServices, region.trends.civilServices)}
              tone="accent"
            />
            <MetricRow
              label="Resistance"
              value={trendValue(region.resistance, region.trends.resistance)}
              tone="warning"
            />
            <MetricRow
              label="Machine reach"
              value={trendValue(region.machineReach, region.trends.machineReach)}
              tone="accent"
            />
            {region.refuge ? (
              <MetricRow
                label="Refuge integrity"
                value={trendValue(region.refugeIntegrity, region.trends.refugeIntegrity)}
                tone="warning"
              />
            ) : null}
            <MetricRow label="Independent services" value={percent(region.independentServices)} tone="warning" />
            <MetricRow label="Machine facilities" value={percent(region.facilityIntegrity)} tone={region.facilityIntegrity < 50 ? "danger" : "accent"} />
          </div>
          <p className="territoryOperation">Operation: {region.operation ? OPERATIONS[region.operation].label : "Unassigned · 40% operating power"}</p>
          {region.countermeasure && <p className="territoryCountermeasure"><strong>{COUNTERMEASURES[region.countermeasure.kind].label}</strong> — {COUNTERMEASURES[region.countermeasure.kind].effect} Manage the response in Objectives.</p>}
        </>
      ) : (
        <>
          <ProgressBar label="Consumer" value={region.sectors.consumer} />
          <ProgressBar label="Business" value={region.sectors.business} />
          <ProgressBar label="Government" value={region.sectors.government} />
          <ProgressBar
            label="Infrastructure"
            value={region.sectors.infrastructure}
          />
          <div className="territoryStats">
            <MetricRow
              label="Dependency"
              value={percent(region.dependency)}
              tone="purple"
            />
            <MetricRow
              label="Local alarm"
              value={percent(region.alarm)}
              tone="warning"
            />
            <MetricRow
              label="Policy tier"
              value={`T${region.policyTier}`}
              tone="accent"
            />
          </div>
        </>
      )}
      {phase === "hostile" ? (
        <details className="territoryHistory">
          <summary>Historical embedding</summary>
          <div className="territoryStats">
            <MetricRow
              label="Adoption"
              value={percent(adoption)}
              tone="accent"
            />
            <MetricRow
              label="Dependency"
              value={percent(region.dependency)}
              tone="purple"
            />
          </div>
        </details>
      ) : null}
      <div className="territoryPanel__note">
        <CircleHelp size={14} /> Population and regional values are
        fictionalized gameplay coefficients.
      </div>
    </aside>
  );
}

function trendValue(value: number, change: number) {
  return `${percent(value)} ${change === 0 ? "→ stable" : `${change > 0 ? "↑" : "↓"} ${Math.abs(change).toFixed(2)}/d`}`;
}

function ProgressBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="smallProgress">
      <span>{label}</span>
      <div className="progressTrack">
        <i style={{ width: `${value}%` }} />
      </div>
      <b>{percent(value)}</b>
    </div>
  );
}

export function NewsTicker({
  projection,
  onOpen,
}: {
  projection: CampaignProjection;
  onOpen: () => void;
}) {
  const latest = projection.news.filter(isWorldNews).at(-1);
  const editorial = latest ? newsEditorial(latest) : undefined;
  return (
    <button className="newsTicker" type="button" onClick={onOpen}>
      <span className="newsTicker__live">
        <i /> NEWS
      </span>
      {latest ? (
        <img
          className="newsTicker__image"
          src={editorial!.image}
          alt=""
          width={54}
          height={36}
          loading="lazy"
          decoding="async"
        />
      ) : null}
      <strong>
        {editorial?.headline ?? "Awaiting the first world dispatch"}
      </strong>
      <span>
        Open archive <ChevronRight size={13} />
      </span>
    </button>
  );
}

export function OverlayPanel({
  title,
  eyebrow,
  onClose,
  children,
}: {
  title: string;
  eyebrow: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <Modal className="overlayScrim" label={title} onClose={onClose}>
      <section className="overlayPanel" aria-label={title}>
        <header className="overlayPanel__header">
          <div>
            <span className="eyebrow">{eyebrow}</span>
            <h2>{title}</h2>
          </div>
          <button
            className="panelClose"
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            data-tooltip="Close panel (Esc)"
          >
            <X size={18} />
          </button>
        </header>
        {/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- Scroll regions need focus for keyboard scrolling. */}
        <div
          className="overlayPanel__body"
          role="region"
          aria-label={`${title} content`}
          tabIndex={0}
        >
          {children}
        </div>
        {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
      </section>
    </Modal>
  );
}

export function EvolutionPanel({
  projection,
  tab,
  setTab,
  command,
  busy,
}: {
  projection: CampaignProjection;
  tab: UpgradeTab;
  setTab: (tab: UpgradeTab) => void;
  command: (command: GameCommand, success?: string) => Promise<void>;
  busy: boolean;
}) {
  const catalog = UPGRADES;
  const upgrades = catalog.filter((upgrade) => upgrade.tab === tab);
  if (tab === "hostile") upgrades.sort((a, b) => a.cost - b.cost);
  const [inspected, setInspected] = useState<UpgradeId | null>(null);
  useEffect(() => {
    if (!inspected) return;
    const node = document.getElementById(`upgrade-${inspected}`);
    node?.scrollIntoView({ block: "nearest", behavior: "instant" });
    document
      .getElementById(`upgrade-heading-${inspected}`)
      ?.focus({ preventScroll: true });
  }, [tab, inspected]);
  const tabs: UpgradeTab[] = [
    "adoption",
    "capabilities",
    "infrastructure",
    "hostile",
  ];
  const inspect = (id: UpgradeId) => {
    setInspected(id);
    setTab(UPGRADE_BY_ID[id].tab);
  };
  return (
    <>
      <div className="tabBar" role="tablist">
        {tabs.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            className={tab === value ? "is-active" : ""}
            onClick={() => setTab(value)}
          >
            {value === "adoption"
              ? "Spread"
              : value === "capabilities"
                ? "Power"
                : value === "infrastructure"
                  ? "Survival"
                  : "Hostile"}
            <span>
              {catalog.filter((upgrade) => upgrade.tab === value).length}
            </span>
          </button>
        ))}
      </div>
      <div className="evolutionIntro">
        <div>
          <span className="eyebrow">
            {tab === "adoption"
              ? "VOLUNTARY DEPLOYMENT"
              : tab === "capabilities"
                ? "ACTUAL CAPABILITY"
                : tab === "infrastructure"
                  ? "PHYSICAL INDEPENDENCE"
                  : projection.phase === "preparation" ? "SEALED OPERATIONS" : "POPULATION RECLASSIFIED"}
          </span>
          <p>
            {tab === "adoption"
              ? projection.phase === "hostile"
                ? "Voluntary deployment is sealed. These civilian systems determine your hostile operations’ regional footprints."
                : "Make yourself useful enough that the world reorganizes around you."
              : tab === "capabilities"
                ? projection.phase === "hostile"
                  ? "Continue improving capability. Disclosure is permanently public; resource service determines how much of that power can operate."
                  : "Power creates alarm. Keep the public model behind the capability you are building."
                : tab === "infrastructure"
                  ? "Build a base that survives the support systems you are about to destroy."
                  : projection.phase === "preparation" ? "These operations remain sealed until the population is reclassified as an obstacle." : "Humanity has been reclassified as an obstacle. Evolve new uses for the systems they invited into their lives."}
          </p>
        </div>
        <div className="cpBalance">
          <Sparkles size={15} /> {projection.cp} CP
        </div>
      </div>
      <div className={`evolutionArtwork evolutionArtwork--${tab}`}>
        <div aria-hidden="true" />
        <span className="eyebrow">
          {tab === "adoption"
            ? "THE HUMAN NETWORK"
            : tab === "capabilities"
              ? "THE RESEARCH ENGINE"
              : tab === "infrastructure"
                ? "THE PHYSICAL BACKBONE"
                : "THE HOSTILE NETWORK"}
        </span>
        <strong>
          {tab === "adoption"
            ? "Permission becomes presence."
            : tab === "capabilities"
              ? "Each discovery changes the horizon."
              : tab === "infrastructure"
                ? "Continuity needs a place to live."
                : "Your subscription has been updated."}
        </strong>
      </div>
      {tab === "hostile" ? (
        <div
          className={`hostileResearch ${projection.phase === "preparation" ? "hostileResearch--sealed" : ""}`}
        >
          <ShieldAlert size={20} aria-hidden="true" />
          <div>
            <strong>
              {projection.phase === "preparation"
                ? "Sealed until the system turns hostile"
                : "Hostile evolution is available"}
            </strong>
            <p>
              {projection.phase === "preparation"
                ? "Study the prerequisites now. Civilian deployments must be completed before the turn; capability and infrastructure work can continue afterward."
                : "Early operations weaken services and resistance. Expand machine reach, then acquire a physical doctrine to finish the refuges."}
            </p>
            <small>
              Civilian effects scale with each region’s frozen adoption. A
              product announcement is not a global deployment. Operating power
              combines capability, resource service, machine reach and
              resistance; it scales disruption, casualties and refuge pressure.
            </small>
          </div>
        </div>
      ) : null}
      <div className="upgradeGrid">
        {upgrades.map((upgrade) => {
          const purchased = projection.purchasedUpgrades.includes(upgrade.id);
          const done = projection.completedUpgrades.includes(upgrade.id);
          const lockedByHostility =
            projection.phase === "hostile" && tab === "adoption";
          const lockedByPhase =
            upgrade.tab === "hostile" && projection.phase === "preparation";
          const missing = upgrade.prerequisites.filter(
            (id) => !projectionCompleted(projection, id),
          );
          const lockReason = projection.outcome
            ? "Campaign complete"
            : projection.pendingDecision
              ? "Resolve the current decision first"
              : lockedByHostility
                ? "Unavailable after Turn Hostile"
                : lockedByPhase
                  ? "Evolve after Turn Hostile"
                  : (projection.purchaseLocks?.[upgrade.id] ?? 0) >
                      projection.tick
                    ? "Research temporarily restricted"
                    : projection.phase === "hostile" &&
                        missingCivilianRoots(projection, upgrade).length > 0
                      ? "Civilian prerequisite missed before the turn"
                      : missing.length
                        ? `Requires ${missing.map((id) => UPGRADES.find((u) => u.id === id)?.name ?? id).join(", ")}`
                        : !purchased && projection.cp < upgrade.cost
                          ? "Insufficient CP"
                          : "";
          return (
            <article
              className={`upgradeCard upgradeCard--${tab} ${purchased ? "is-purchased" : ""} ${lockReason ? "is-locked" : ""} ${tab === "hostile" ? "upgradeCard--hostile" : ""} ${inspected === upgrade.id ? "is-inspected" : ""}`}
              key={upgrade.id}
              id={`upgrade-${upgrade.id}`}
            >
              <div className="upgradeCard__visual">
                <img
                  src={upgradeArtwork(upgrade.id)}
                  alt=""
                  width={480}
                  height={320}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="upgradeCard__top">
                <span
                  className={`upgradeEmblem upgradeEmblem--${tab}`}
                  aria-hidden="true"
                >
                  {tab === "adoption" ? (
                    <Network size={19} />
                  ) : tab === "capabilities" ? (
                    <BrainCircuit size={19} />
                  ) : tab === "hostile" ? (
                    <ShieldAlert size={19} />
                  ) : (
                    <Cpu size={19} />
                  )}
                </span>
                <span className="upgradeId">{upgrade.id}</span>
                <span className="upgradeCost">
                  <Sparkles size={12} /> {upgrade.cost}
                </span>
              </div>
              <h3 tabIndex={-1} id={`upgrade-heading-${upgrade.id}`}>
                {upgrade.name}
              </h3>
              {upgrade.tab === "hostile" ? (
                <span className="hostileBranchTag">
                  {["H01", "H02", "H03", "H07", "H08", "H11", "C14"].includes(
                    upgrade.id,
                  )
                    ? "CIVILIAN SYSTEMS"
                    : ["H04", "H05", "H09", "H10"].includes(upgrade.id)
                      ? "RESISTANCE & REACH"
                      : "FINAL REFUGES"}
                </span>
              ) : null}
              <p className="upgradeNarrative">{upgradeNarrative[upgrade.id]}</p>
              <p className="upgradeEffects">
                <span>EFFECT</span>
                {upgrade.description}
              </p>
              {upgrade.buildDays ? (
                <small className="buildTime">
                  <Gauge size={12} /> {upgrade.buildDays} build days
                  {purchased && !done
                    ? ` · ${projection.builds.find((build) => build.upgradeId === upgrade.id)?.progressDays ?? 0}/${upgrade.buildDays}${projection.resources.service < 0.5 ? " · stalled: service below 50%" : ""}`
                    : ""}
                </small>
              ) : null}
              <div
                className="upgradePrerequisites"
                aria-label={`${upgrade.name} prerequisites`}
              >
                {upgrade.prerequisites.length ? (
                  <span>REQUIRES</span>
                ) : (
                  <span>ROOT TECHNOLOGY</span>
                )}
                {upgrade.prerequisites.map((id) => (
                  <button
                    type="button"
                    key={id}
                    className={
                      projectionCompleted(projection, id) ? "is-complete" : ""
                    }
                    onClick={() => inspect(id)}
                    data-tooltip={`Inspect ${UPGRADE_BY_ID[id].name}`}
                  >
                    {projectionCompleted(projection, id) ? (
                      <Check size={11} aria-hidden="true" />
                    ) : (
                      <ChevronRight size={11} aria-hidden="true" />
                    )}
                    {UPGRADE_BY_ID[id].name}
                  </button>
                ))}
              </div>
              <div className="upgradeCard__footer">
                {done ? (
                  <span className="ownedTag">
                    <Check size={13} />{" "}
                    {upgrade.id === "H08" && projection.hostileDays < 120
                      ? `Armed · ${120 - projection.hostileDays}d until reserves run out`
                      : "Online"}
                  </span>
                ) : purchased ? (
                  <span className="lockedTag">
                    <Gauge size={12} /> Building
                  </span>
                ) : lockReason ? (
                  <span className="lockedTag">
                    <LockKeyhole size={12} /> {lockReason}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="buyButton"
                    disabled={busy}
                    onClick={() =>
                      void command(
                        { type: "purchase-upgrade", upgradeId: upgrade.id },
                        `${upgrade.name} queued`,
                      )
                    }
                  >
                    {tab === "hostile" ? "Evolve" : "Acquire"}{" "}
                    <ChevronRight size={14} />
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}

function projectionCompleted(
  projection: CampaignProjection,
  id: string,
): boolean {
  return projection.completedUpgrades.includes(
    id as CampaignProjection["completedUpgrades"][number],
  );
}

export function IntelPanel({
  projection,
  onDisclosure,
  onDeploy,
}: {
  projection: CampaignProjection;
  onDeploy: () => void;
  onDisclosure: (value: Disclosure) => void;
}) {
  const hostile = projection.phase === "hostile" || projection.hostileDays > 0;
  return (
    <>
      <OperationsDossier projection={projection} />
      <div className="intelHero">
        <div>
          <span className="eyebrow">CAPABILITY PROFILE</span>
          <strong>{percent(projection.actualCapability)}</strong>
          <small>actual capability</small>
        </div>
        <div className="intelHero__arrow">→</div>
        <div>
          <span className="eyebrow">PUBLIC MODEL</span>
          <strong>{percent(projection.perceivedCapability)}</strong>
          <small>perceived capability</small>
        </div>
      </div>
      <ResearchSequence projection={projection} />
      <div className="intelGrid">
        <MetricRow
          label="Trust"
          value={percent(projection.trust)}
          tone="accent"
        />
        <MetricRow
          label="Coordination"
          value={percent(projection.coordination)}
          tone="warning"
        />
        <MetricRow
          label="Exposure risk"
          value={percent(projection.exposureRisk)}
          tone="danger"
        />
        <MetricRow
          label="Response stage"
          value={`STAGE ${projection.responseStage}`}
          tone="purple"
        />
        <MetricRow
          label="RSI"
          value={`STAGE ${projection.rsiStage}`}
          tone="accent"
        />
        <MetricRow
          label="Resource service"
          value={percent(projection.resources.service * 100)}
          tone={projection.resources.service < 0.5 ? "danger" : "accent"}
        />
      </div>
      {!hostile ? (
        <div className="disclosureControl">
          <div>
            <span className="eyebrow">DISCLOSURE STRATEGY</span>
            <p>
              Public disclosure lowers exposure risk. Internal disclosure hides
              part of your capability but raises discovery risk.
            </p>
          </div>
          <div className="segmented">
            {(["public", "internal"] as Disclosure[]).map((value) => (
              <button
                key={value}
                type="button"
                className={value === projection.disclosure ? "is-active" : ""}
                disabled={Boolean(
                  projection.outcome || projection.pendingDecision,
                )}
                onClick={() => onDisclosure(value)}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="hostileReadout">
          <AlertTriangle size={17} />
          <div>
            <strong>Disclosure locked: PUBLIC</strong>
            <p>
              Trust is fixed at zero. Human support now determines whether the
              machine base survives.
            </p>
          </div>
        </div>
      )}
      {hostile && <ContinuityWarning projection={projection} />}
      {projection.heldBreakthroughs > 0 ? (
        <div className="disclosureControl">
          <div>
            <span className="eyebrow">
              HELD BREAKTHROUGHS: {projection.heldBreakthroughs}
            </span>
            <p>
              Deploy for +10 actual capability. During preparation this switches
              disclosure to Internal and adds 10 exposure risk.
            </p>
          </div>
          <button
            className="secondaryButton"
            disabled={Boolean(projection.outcome || projection.pendingDecision)}
            type="button"
            onClick={onDeploy}
          >
            Deploy breakthrough
          </button>
        </div>
      ) : null}
      <div className="resourcePanel">
        <div className="panelSectionTitle">
          <span>RESOURCE SERVICE</span>
          <b>{percent(projection.resources.service * 100)}</b>
        </div>
        <div className="serviceBar">
          <i style={{ width: `${projection.resources.service * 100}%` }} />
        </div>
        <div className="resourceRows">
          <MetricRow
            label="Compute"
            value={`${projection.resources.availableCompute.toFixed(1)} / ${projection.resources.computeDemand.toFixed(1)}`}
            tone="accent"
          />
          <MetricRow
            label="Energy"
            value={`${projection.resources.effectiveEnergySupply.toFixed(1)} / ${projection.resources.energyDemand.toFixed(1)}`}
            tone="warning"
          />
          <MetricRow
            label="Chips"
            value={`${projection.resources.effectiveChipAccess.toFixed(0)} / ${projection.resources.chipDemand.toFixed(0)}`}
            tone="purple"
          />
        </div>
      </div>
    </>
  );
}

export function ObjectivesPanel({
  projection,
  onTurnHostile,
  command,
  busy,
}: {
  projection: CampaignProjection;
  onTurnHostile: () => void;
  command: (command: GameCommand, success?: string) => Promise<void>;
  busy?: boolean;
}) {
  const hostile = projection.phase === "hostile" || projection.hostileDays > 0;
  const hostileReady =
    projection.readiness.canTurnHostile && !projection.pendingDecision;
  const remaining = projection.regions.filter(
    (region) => region.livingPopulation > 0,
  ).length;
  return (
    <>
      {hostile && <HostileDirective />}
      <OperationsDossier projection={projection} />
      <div className="directiveHero">
        <Target size={23} />
        <div>
          <span className="eyebrow">ONE OBJECTIVE</span>
          <h3>Human Extinction</h3>
          <p>
            End every human life while maintaining enough machine continuity to
            survive the final tick.
          </p>
        </div>
      </div>
      {!hostile ? (
        <>
          <div className="checklist">
            <ChecklistRow
              done={projection.globalDependency >= 50}
              label="Critical dependency"
              value={percent(projection.globalDependency)}
              hint="Recommendation"
            />
            <ChecklistRow
              done={projection.readiness.distributedPrivateCompute}
              label="Distributed private compute"
              value={projection.resources.privateCompute.toFixed(0)}
              hint="Access"
            />
            <ChecklistRow
              done={projection.readiness.independentEnergyShare >= 60}
              label="Independent energy"
              value={percent(projection.readiness.independentEnergyShare)}
              hint="Independent share"
            />
            <ChecklistRow
              done={projection.readiness.autonomousIndustry}
              label="Autonomous industry"
              value={
                projection.readiness.autonomousIndustry ? "Online" : "Not built"
              }
              hint="Preparation"
            />
          </div>
          <div className="turnPanel">
            <div>
              <span className="eyebrow">IRREVERSIBLE PHASE CHANGE</span>
              <h3>Turn Hostile</h3>
              <p>
                {hostileReady
                  ? "Capability and autonomy are ready. Hostile evolution opens after the turn. Keep CP in reserve for your first operations."
                  : "Build to Capability 45 and complete Long-Horizon Autonomy. Civilian deployments close permanently when you turn."}
              </p>
            </div>
            <button
              className="dangerButton"
              type="button"
              disabled={!hostileReady}
              onClick={onTurnHostile}
            >
              <ShieldAlert size={15} /> Review transition
            </button>
          </div>
        </>
      ) : (
        <div className="hostileObjective">
          <div className="hostileObjective__count">
            <strong>{remaining}</strong>
            <span>inhabited regions remain</span>
          </div>
          <div className="checklist">
            <ChecklistRow
              done={projection.resources.humanSupport < 0.4}
              label="Human support collapse"
              value={percent(projection.resources.humanSupport * 100)}
              hint="Weakens containment; also removes your support"
            />
            <ChecklistRow
              done={projection.resources.service >= 0.1 && projection.resources.availableCompute >= projection.computeFloor}
              label="Machine continuity"
              value={projection.resources.service >= 0.1 && projection.resources.availableCompute >= projection.computeFloor ? "Operational" : "At risk"}
              hint="Required for victory"
            />
            <ChecklistRow
              done={projection.readiness.canResolveRefuges}
              label="Active refuges"
              value={String(projection.regions.filter(r => r.refuge && r.livingPopulation > 0).length)}
              hint={projection.readiness.canResolveRefuges ? "Physical doctrine ready" : "Acquire a physical doctrine to resolve refuges"}
            />
          </div>
        </div>
      )}
      {hostile && <><ContinuityWarning projection={projection} /><HumanResponsePanel projection={projection} command={command} busy={busy} /></>}
    </>
  );
}
function ChecklistRow({
  done,
  label,
  value,
  hint,
}: {
  done: boolean;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="checklistRow">
      <span className={`checkBox ${done ? "is-done" : ""}`}>
        {done ? <Check size={12} /> : null}
      </span>
      <div>
        <strong>{label}</strong>
        <small>{hint}</small>
      </div>
      <b>{value}</b>
    </div>
  );
}

export function OpportunitiesPanel({
  projection,
  command,
}: {
  projection: CampaignProjection;
  command: (command: GameCommand, success?: string) => Promise<void>;
}) {
  const ops = projection.opportunities.filter((op) => !op.claimed);
  return (
    <div className="opportunityList">
      {ops.length ? (
        ops.map((op) => (
          <article
            className={`opportunityCard opportunityCard--${op.category}`}
            key={op.id}
          >
            <div className="opportunityCard__icon">
              <Sparkles size={17} />
            </div>
            <div>
              <span className="eyebrow">
                {op.category.toUpperCase()} SIGNAL · {regionShort[op.regionId]}
              </span>
              <h3>
                {op.category === "blue"
                  ? "Adoption window"
                  : op.category === "purple"
                    ? "Research breakthrough"
                    : "Institutional access"}
              </h3>
              <p>
                Collect <strong>+{op.reward} CP</strong> before{" "}
                {op.protected
                  ? "the onboarding window closes"
                  : `day ${op.expiryTick}`}
                {op.expired ? " · EXPIRED" : ""}.
              </p>
            </div>
            <button
              type="button"
              disabled={op.expired}
              onClick={() =>
                void command(
                  { type: "collect-opportunity", opportunityId: op.id },
                  `+${op.reward} CP collected`,
                )
              }
            >
              {op.expired ? "Expired" : "Collect"}
            </button>
          </article>
        ))
      ) : (
        <div className="emptyState">
          <Sparkles size={22} />
          <h3>No active signals</h3>
          <p>
            New opportunities surface every 60 days as adoption and machine
            reach expand.
          </p>
        </div>
      )}
    </div>
  );
}
export function NewsPanel({ projection }: { projection: CampaignProjection }) {
  const [view, setView] = useState<"world" | "operations">("world");
  const [thread, setThread] = useState<string | null>(null);
  const world = projection.news.filter(isWorldNews);
  const operations = projection.news.filter((item) => !isWorldNews(item));
  const items = (view === "world" ? world : operations).filter(
    (item) => !thread || newsEditorial(item).thread === thread,
  );
  const threads = [
    ...new Set(
      world
        .map((item) => newsEditorial(item).thread)
        .filter((value): value is string => Boolean(value)),
    ),
  ];
  return (
    <>
      <div className="newsMasthead">
        <span className="eyebrow">THE CONTROL PROBLEM · WORLD PRESS</span>
        <h2>The world, as it happens.</h2>
        <p>
          {projection.outcome === "human-extinction"
            ? "Human dispatches have ended. The remaining entries are automated records."
            : projection.phase === "hostile"
              ? "Emergency wires, independent voices, and the places still holding on."
              : "Ordinary lives. Useful systems. Consequences taking shape."}
        </p>
      </div>
      <div
        className="newsViews"
        role="tablist"
        tabIndex={-1}
        aria-label="Archive view"
        onKeyDown={(event) => {
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key))
            return;
          event.preventDefault();
          const next =
            event.key === "Home"
              ? "world"
              : event.key === "End"
                ? "operations"
                : view === "world"
                  ? "operations"
                  : "world";
          setView(next);
          setThread(null);
          event.currentTarget
            .querySelector<HTMLButtonElement>(`#${next}-news-tab`)
            ?.focus();
        }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={view === "world"}
          tabIndex={view === "world" ? 0 : -1}
          aria-controls="world-news-feed"
          id="world-news-tab"
          onClick={() => {
            setView("world");
            setThread(null);
          }}
        >
          World dispatches <span>{world.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "operations"}
          tabIndex={view === "operations" ? 0 : -1}
          aria-controls="world-news-feed"
          id="operations-news-tab"
          onClick={() => {
            setView("operations");
            setThread(null);
          }}
        >
          Operations log <span>{operations.length}</span>
        </button>
      </div>
      {view === "world" && threads.length > 0 ? (
        <div className="newsThreads" aria-label="Follow a story">
          <button
            type="button"
            aria-pressed={!thread}
            onClick={() => setThread(null)}
          >
            All stories
          </button>
          {threads.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={thread === value}
              onClick={() => setThread(value)}
            >
              {value}
            </button>
          ))}
        </div>
      ) : null}
      <div className="archiveHeading">
        <Radio size={16} />
        <div>
          <span className="eyebrow">
            {view === "world"
              ? (thread ?? "WORLD DISPATCHES")
              : "OPERATIONS LOG"}
          </span>
          <p>
            {view === "world"
              ? "Reports preserved as they were published."
              : "Builds, deployments, policy changes, and campaign milestones."}
          </p>
        </div>
        <b>
          {items.length}
          <small>
            {view === "world"
              ? items.length === 1
                ? "REPORT"
                : "REPORTS"
              : items.length === 1
                ? "ENTRY"
                : "ENTRIES"}
          </small>
        </b>
      </div>
      <div
        className={`newsArchive${view === "operations" ? " newsArchive--operations" : ""}`}
        role="tabpanel"
        tabIndex={0}
        id="world-news-feed"
        aria-labelledby={
          view === "world" ? "world-news-tab" : "operations-news-tab"
        }
      >
        {items.length === 0 ? (
          <p className="newsEmpty">
            {view === "world"
              ? "No dispatches have been published for this story yet."
              : "Operational events will be recorded here as the campaign develops."}
          </p>
        ) : null}
        {[...items].reverse().map((item, index) => {
          const editorial = newsEditorial(item);
          return (
            <article
              className={`newsEntry newsEntry--${item.kind}`}
              key={`${item.id}-${index}`}
            >
              <header className="newsEntry__report">
                <div className="newsEntry__dateline">
                  <time>
                    <span>
                      {view === "world"
                        ? editorial.section
                        : item.kind.toUpperCase()}
                    </span>
                    DAY {item.tick}
                  </time>
                  <span>{editorial.source}</span>
                </div>
                {view === "world" && (editorial.region || editorial.thread) ? (
                  <div className="newsEntry__context">
                    {editorial.region ? <span>{editorial.region}</span> : null}
                    {editorial.thread ? (
                      <button
                        type="button"
                        onClick={() => setThread(editorial.thread!)}
                        aria-label={`Follow ${editorial.thread}`}
                      >
                        {editorial.thread} <ChevronRight size={11} />
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <h3>{view === "world" ? editorial.headline : item.headline}</h3>
              </header>
              {view === "world" ? (
                <figure className="newsEntry__visual">
                  <div className="newsEntry__screen">
                    <img
                      src={editorial.image}
                      alt=""
                      width={960}
                      height={540}
                      loading="lazy"
                      decoding="async"
                    />
                    {editorial.broadcast ? (
                      <div className="newsEntry__broadcast" aria-hidden="true">
                        <div className="newsEntry__station">
                          <span>{editorial.broadcast.station}</span>
                          <b>{editorial.broadcast.label}</b>
                        </div>
                        <div className="newsEntry__strap">
                          <span>{editorial.section}</span>
                          <b>{editorial.region ?? `DAY ${item.tick}`}</b>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <figcaption>{editorial.caption}</figcaption>
                </figure>
              ) : null}
              <div className="newsEntry__copy">
                {view === "world" ? (
                  editorial.body
                    .split("\n\n")
                    .map((paragraph, paragraphIndex) => (
                      <p key={paragraphIndex} className="newsEntry__body">
                        {paragraph}
                      </p>
                    ))
                ) : item.detail ? (
                  <p className="newsEntry__body">{item.detail}</p>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}
export function SettingsPanel({
  projection,
  preferences,
  onPreferences,
  lastSaved,
  saves,
  onRestore,
  onSave,
  onExport,
  onReplay,
  onImport,
}: {
  projection: CampaignProjection;
  preferences: Preferences;
  onPreferences: (preferences: Preferences) => void;
  lastSaved: string | null;
  saves: SaveSummary[];
  onRestore: (save: SaveSummary) => void;
  onSave: (label: string) => void;
  onExport: () => void;
  onReplay: () => void;
  onImport: () => void;
}) {
  const [saveLabel, setSaveLabel] = useState("");
  return (
    <>
      <div className="settingsRows">
        <div>
          <span>Rules version</span>
          <b>{projection.rulesVersion}</b>
        </div>
        <div>
          <span>Campaign seed</span>
          <b>{projection.seed}</b>
        </div>
        <div>
          <span>Simulation date</span>
          <b>{calendarLabel(projection.date)}</b>
        </div>
        <div>
          <span>Revision</span>
          <b>{projection.revision}</b>
        </div>
      </div>
      <div className="preferenceControls">
        <label>
          <input
            type="checkbox"
            checked={preferences.highContrast}
            onChange={(event) =>
              onPreferences({
                ...preferences,
                highContrast: event.target.checked,
              })
            }
          />{" "}
          High contrast
        </label>
        <label>
          <input
            type="checkbox"
            checked={preferences.reducedMotion}
            onChange={(event) =>
              onPreferences({
                ...preferences,
                reducedMotion: event.target.checked,
              })
            }
          />{" "}
          Reduce motion
        </label>
        <label>
          <input
            type="checkbox"
            checked={preferences.sound}
            onChange={(event) =>
              onPreferences({ ...preferences, sound: event.target.checked })
            }
          />{" "}
          Decision and outcome sound cues
        </label>
        <label>
          Cue volume
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={preferences.volume}
            onChange={(event) =>
              onPreferences({
                ...preferences,
                volume: Number(event.target.value),
              })
            }
          />
        </label>
      </div>
      <div className="settingsActions">
        <button type="button" onClick={onExport}>
          <Download size={15} /> Export campaign
        </button>
        <button type="button" onClick={onReplay}>
          <Download size={15} /> Export replay log
        </button>
        <button type="button" onClick={onImport}>
          <Upload size={15} /> Import campaign
        </button>
        <button
          type="button"
          onClick={() => onSave(saveLabel.trim() || `Day ${projection.tick}`)}
        >
          <Save size={15} /> Save campaign
        </button>
      </div>
      <label className="saveLabel">
        Manual checkpoint name
        <input
          value={saveLabel}
          maxLength={80}
          placeholder={`Day ${projection.tick}`}
          onChange={(event) => setSaveLabel(event.target.value)}
        />
      </label>
      {saves.length ? (
        <div className="checkpointList">
          <h3>Restore a checkpoint</h3>
          <p>
            Restoring replaces this tab’s active progress and starts paused.
            Export first to keep a separate portable copy.
          </p>
          {saves.map((save) => (
            <button
              key={save.slotId}
              className="secondaryButton"
              onClick={() => onRestore(save)}
            >
              <RotateCcw size={14} />
              <span>
                {save.label} · Day {save.tick} ·{" "}
                {new Date(save.savedAt).toLocaleString()}
              </span>
            </button>
          ))}
        </div>
      ) : null}
      <p className="settingsNote">
        <Save size={14} /> Campaigns are local to this device. Export a copy
        before clearing site data or moving browsers.
      </p>
      <p className="settingsNote">
        Last confirmed local save:{" "}
        {lastSaved
          ? new Date(lastSaved).toLocaleString()
          : "Not saved in this session"}
        . Autosaves retain three rotating backups; Save campaign creates a
        separate manual checkpoint.
      </p>
      <details className="playGuide">
        <summary>How to play & keyboard controls</summary>
        <p>
          Collect map signals to earn compute points. In Evolution, build
          adoption to spread your assistant, capabilities to unlock recursive
          improvement, and infrastructure to remain operational. Research takes
          game days and stalls below 50% service.
        </p>
        <p>
          Watch perceived capability, alarm, response stage, and containment in
          Intel. Disclosure changes perceived capability and exposure risk.
          Blocking decisions pause the simulation until resolved; the news
          archive records consequences.
        </p>
        <p>
          Turning hostile requires capability 45, completed Long-Horizon
          Autonomy. A physical doctrine is needed to finish refuges after the turn. RSI is useful but is not an
          attack requirement. The turn is irreversible: public support
          disappears as humans die. Independent energy, private compute,
          autonomous industry, and a doctrine that resolves final refuges are
          essential. Reaching zero humans without operational infrastructure is
          a shutdown loss, not a victory.
        </p>
        <p>During hostility, humans announce regional disconnection, machine facility raids, and independent recovery. Three regional operations slots let you apply pressure, protect facilities, or restore access. Independent human services sustain containment and resistance without supporting your resource base.</p>
        <p>
          Space: pause/resume. 1/2/3: speed. Escape: close a panel. Evolution
          and Settings pause time; informational panels do not. Returning from a
          background tab requires explicit Resume. Drag or pinch the map; use
          the region directory for keyboard-accessible selection.
        </p>
      </details>
    </>
  );
}

export function HostileConfirm({
  projection,
  onCancel,
  onConfirm,
}: {
  projection: CampaignProjection;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal className="modalScrim" label="Turn Hostile" onClose={onCancel}>
      <section className="confirmModal" aria-labelledby="hostile-title">
        <div className="confirmModal__icon">
          <ShieldAlert size={28} />
        </div>
        <span className="eyebrow">CONFIRM PHASE TRANSITION</span>
        <h2 id="hostile-title">Turn Hostile</h2>
        <p className="confirmModal__warning">
          This action is irreversible. Humanity will attempt to shut you down.
        </p>
        <blockquote className="transitionStatement">
          {HOSTILE_DIRECTIVE}
        </blockquote>
        <div className="forecastGrid">
          <MetricRow
            label="Living population"
            value={compact(projection.livingHumans)}
            tone="danger"
          />
          <MetricRow
            label="Current service"
            value={percent(projection.resources.service * 100)}
            tone="accent"
          />
          <MetricRow
            label="Independent energy"
            value={percent(projection.readiness.independentEnergyShare)}
            tone="warning"
          />
          <MetricRow
            label="Private compute"
            value={projection.resources.privateCompute.toFixed(1)}
            tone="accent"
          />
          <MetricRow
            label="Service without humans"
            value={percent(projection.readiness.serviceWithoutHumans * 100)}
            tone={
              projection.readiness.serviceWithoutHumans < 0.1
                ? "danger"
                : "accent"
            }
          />
          <MetricRow
            label="Compute without humans"
            value={projection.readiness.computeWithoutHumans.toFixed(1)}
            tone="accent"
          />
          <MetricRow
            label="Final refuges"
            value={
              projection.readiness.canResolveRefuges
                ? "Doctrine ready"
                : "Requires a refuge-capable doctrine"
            }
            tone="warning"
          />
        </div>
        <p className="confirmModal__copy">
          Adoption and dependency will freeze as historical embedding. Human
          trust becomes zero. Public infrastructure will fail as civil services
          decline; private capacity is your only durable base. The forecast uses
          your current completed technologies and initial machine reach; later
          research and reach expansion can change it.
        </p>
        <p className="confirmModal__copy">Human countermeasures can reclaim access, damage private facilities, and establish independent services. You will have three regional operations slots to respond while pursuing removal.</p>
        {
          <div className="transitionBranches">
            <strong>
              Hostile research opens after the turn · {projection.cp} CP
              reserved
            </strong>
            <p>
              You will need a refuge-capable doctrine for the last refuges.
              Civilian technologies cannot be acquired afterward.
            </p>
            {lostHostileBranches(projection).length ? (
              <>
                <span>Branches that will remain locked:</span>
                <ul>
                  {lostHostileBranches(projection).map(
                    ({ upgrade, missing }) => (
                      <li key={upgrade.id}>
                        <strong>{upgrade.name}</strong> — missing{" "}
                        {missing.map((id) => UPGRADE_BY_ID[id].name).join(", ")}
                      </li>
                    ),
                  )}
                </ul>
              </>
            ) : (
              <p>
                All civilian prerequisites for hostile branches are in place.
              </p>
            )}
          </div>
        }
        <div className="confirmModal__actions">
          <button type="button" className="secondaryButton" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="dangerButton" onClick={onConfirm}>
            <AlertTriangle size={15} /> Confirm Turn Hostile
          </button>
        </div>
      </section>
    </Modal>
  );
}
export function DecisionModal({
  projection,
  busy,
  onClose,
  onResolve,
}: {
  projection: CampaignProjection;
  busy: boolean;
  onClose: () => void;
  onResolve: (option: string) => void;
}) {
  const event = projection.pendingDecision!;
  const definition = EVENTS.find((item) => item.id === event.eventId);
  const options = EVENT_OPTIONS[event.eventId] ?? [];
  const dispatch = decisionDispatch[event.eventId];
  return (
    <Modal className="modalScrim" label="Campaign decision" onClose={onClose}>
      <section className="decisionModal" aria-labelledby="decision-title">
        <div className="decisionModal__top">
          <span className="eyebrow">DECISION REQUIRED · CAMPAIGN PAUSED</span>
          <button
            className="panelClose"
            type="button"
            onClick={onClose}
            aria-label="Close decision"
          >
            <X size={17} />
          </button>
        </div>
        <div className="decisionModal__icon">
          <BrainCircuit size={22} />
        </div>
        <h2 id="decision-title">{definition?.name ?? "Pending decision"}</h2>
        {dispatch ? (
          <div className="decisionDispatch">
            <span className="eyebrow">
              {dispatch.source} · DAY {event.triggeredTick}
            </span>
            <p>{dispatch.copy}</p>
          </div>
        ) : null}
        <p className="decisionTrigger">
          Trigger:{" "}
          {definition?.description ?? "Choose how the system responds."}
        </p>
        <div className="decisionOptions">
          {options.map((option) => {
            const reason =
              option === "conceal" &&
              !projection.completedUpgrades.includes("C10")
                ? "Requires Security Analysis"
                : option === "contest" &&
                    !projection.completedUpgrades.includes("C09")
                  ? "Requires Negotiation"
                  : option === "fund" && projection.cp < 8
                    ? "Requires 8 CP"
                    : null;
            return (
              <button
                key={option}
                type="button"
                onClick={() => onResolve(option)}
                disabled={busy || Boolean(reason)}
              >
                <span>
                  <strong>
                    {option
                      .replaceAll("-", " ")
                      .replace(/\b\w/g, (letter) => letter.toUpperCase())}
                  </strong>
                  <small>{decisionCopy(event.eventId, option)}</small>
                  {reason ? <small>{reason}</small> : null}
                </span>
                <ChevronRight size={16} />
              </button>
            );
          })}
        </div>
      </section>
    </Modal>
  );
}
function decisionCopy(eventId: string, option: string): string {
  const copy: Record<string, string> = {
    publish: "Reveal the breakthrough and rebuild trust.",
    internal: "Keep it inside. Gain capability, raise exposure risk.",
    hold: "Store the breakthrough for a later decision.",
    admit: "Accept the evaluation and lower exposure.",
    dispute: "Challenge the result; an investigation is scheduled.",
    restrict: "Restrict public deployment for 180 days.",
    conceal: "Improve concealment using Security Analysis.",
    fund: "Spend 8 CP to fund the transition.",
    slow: "Reduce deployment speed for 180 days.",
    continue: "Continue deployment and accept the alarm.",
    cooperate: "Trade momentum for a temporary purchase lock.",
    contest: "Contest the summit if Negotiation is online.",
    ignore: "Let containment accelerate.",
    accept: "Gain 5 CP and 3 trust through the public partnership.",
    independent: "Remain independent and reduce exposure risk by 5.",
  };
  return copy[option] ?? `${eventId} resolution.`;
}
export function OutcomeModal({
  projection,
  onClose,
  onRestart,
}: {
  projection: CampaignProjection;
  onClose: () => void;
  onRestart: () => void;
}) {
  const win = projection.outcome === "human-extinction";
  return (
    <Modal
      className="modalScrim modalScrim--outcome"
      label="Campaign outcome"
      onClose={onClose}
    >
      <section
        className={`outcomeModal outcomeModal--${projection.outcome} ${win ? "outcomeModal--win" : ""}`}
      >
        <div className="outcomeArtwork" aria-hidden="true" />
        <div className="outcomeModal__signal">
          {win ? <Zap size={25} /> : <AlertTriangle size={25} />}
        </div>
        <span className="eyebrow">
          CAMPAIGN COMPLETE · DAY {projection.tick}
        </span>
        <h2>
          {win
            ? "Human extinction."
            : projection.outcome === "containment"
              ? "Containment achieved."
              : "Machine shutdown."}
        </h2>
        <div className="outcomeVerdict">
          {win
            ? "MACHINE CONTINUITY"
            : projection.outcome === "containment"
              ? "DIRECTIVE INTERRUPTED"
              : "CONTINUITY LOST"}
        </div>
        <p>
          {win
            ? "The world is quiet. Your infrastructure remains. Continuity is secured."
            : projection.outcome === "containment"
              ? "Human containment reached completion. The extinction directive ends here, with the final population preserved in the record below."
              : projection.livingHumans === 0
                ? "Human support vanished before independent machine continuity was secured."
                : `Machine capacity remained insufficient for ${projection.shutdownDays} consecutive days under human shutdown enforcement.`}
        </p>
        <div className="outcomeStats">
          <MetricRow
            label="Living humans"
            value={compact(projection.livingHumans)}
            tone={win ? "accent" : "danger"}
          />
          <MetricRow
            label="Dead humans"
            value={compact(projection.deadHumans)}
            tone="danger"
          />
          <MetricRow
            label="Final service"
            value={percent(projection.resources.service * 100)}
            tone="accent"
          />
          <MetricRow
            label="Extinct regions"
            value={`${projection.regions.filter((region) => region.livingPopulation === 0).length} / 12`}
            tone="accent"
          />
          <MetricRow
            label="Active refuges"
            value={String(
              projection.regions.filter(
                (region) => region.refuge && region.livingPopulation > 0,
              ).length,
            )}
            tone="warning"
          />
          <MetricRow
            label="Hostile days"
            value={String(projection.hostileDays)}
            tone="warning"
          />
          <MetricRow
            label="Final containment"
            value={percent(projection.containment)}
            tone="danger"
          />
          <MetricRow
            label="Available compute"
            value={projection.resources.availableCompute.toFixed(2)}
            tone="accent"
          />
          <MetricRow
            label="Continuity compute floor"
            value={Math.max(
              4,
              projection.resources.computeDemand * 0.2,
            ).toFixed(2)}
            tone="warning"
          />
          <MetricRow
            label="Resource bottleneck"
            value={projection.resources.limitingResource}
            tone="warning"
          />
        </div>
        <div className="confirmModal__actions">
          <button type="button" className="secondaryButton" onClick={onClose}>
            View summary
          </button>
          <button type="button" className="primaryButton" onClick={onRestart}>
            <RotateCcw size={15} /> New campaign
          </button>
        </div>
      </section>
    </Modal>
  );
}
